/**
 * Rive layer for the Baccarat port.
 *
 * Mirrors packages/amgbaccarat_v2/lib/presentation/rive/rive_controller_manager.dart.
 *
 *     main    → baccarat.riv          / MainBaccarat / Main
 *     card    → baccarat.riv          / noHIstory    / Card   (card reveal + scores)
 *     betting → baccaratbettingui.riv / MainBaccarat / Main   (bet areas only)
 *     top     → baccarat.riv          / noHIstory    / Main   (balance/bet/win texts + winner banner)
 *     winlose → MainWinLose.riv       / Button       / animationManager
 *                              (StartBet / NoMoreBet + YOU WIN / YOU LOSE popups)
 *
 * Key deltas vs Dragon Tiger:
 *   - StartBet / NoMoreBet popups live on the MainWinLose.riv `winlose` overlay,
 *     driven by a unified BetPanelNumber (1=StartBet, 2=NoMoreBet) + BetPanelTrigger
 *     (the designer moved them off the main artboard). Winner banners are still
 *     separate triggers: triPlayerWin/triBankerWin/triTie on top;
 *     trigWinPlayer/trigWinBanker/trigWinTie on betting.
 *   - Up to 3 cards per side via PlayerCard1/2/3 + BankerCard1/2/3,
 *     all nested at path "CardType". 0 = absent.
 *   - Card reveal driven by CardPlayerBanker (number) + trigger pair,
 *     fired on both `card` and `top` controllers. Codes:
 *       P1=1, B1=2, P2=4, B2=5, P3=3, B3=6.
 *   - YOU WIN / YOU LOSE popup on the MainWinLose.riv `winlose` overlay (the
 *     same overlay as StartBet / NoMoreBet): txtWonAmount text + YouWinLose
 *     number (1=win,2=lose) + YouWinLoseTrigger; the overlay emits evtYouWinEnd
 *     / evtYouLoseEnd when it finishes. (NOT the betting artboard — this port's
 *     baccaratbettingui.riv is a bet-zones-only export with no win/lose graphic.)
 *   - 96-slot history (vs Dragon Tiger's 10): numHistory1..96 on main.
 */
import {
  Rive,
  Layout,
  Fit,
  Alignment,
  EventType,
  StateMachineInputType,
} from '@rive-app/webgl2';

import { debugLog, warn } from '../util/logger';
import {
  BettingInputs,
  BetPanelState,
  CardInputs,
  HistoryInputs,
  MainInputs,
  ScoreInputs,
  TopInputs,
  WinLoseInputs,
  type RiveSide,
} from './riveInputs';

type AnyInput =
  | { type: 'number'; name: string; set(v: number): void; get(): number }
  | { type: 'boolean'; name: string; set(v: boolean): void; get(): boolean }
  | { type: 'trigger'; name: string; fire(): void };

interface RiveTarget {
  key: TargetKey;
  file: string;
  stateMachine: string;
  instance: Rive;
  inputs: Map<string, AnyInput>;
}

export type TargetKey = 'main' | 'card' | 'betting' | 'top' | 'winlose';

/**
 * Where a text run actually lives: on the artboard of `key` either as a
 * top-level run (no `path`) or nested under the `path` artboard instance.
 */
interface TextRunPlacement {
  key: TargetKey;
  path?: string;
}

const FILES = {
  main: 'baccarat.riv',
  card: 'baccarat.riv',
  betting: 'baccaratbettingui.riv',
  top: 'baccarat.riv',
  winlose: 'MainWinLose.riv',
} as const;

const ARTBOARDS = {
  main: 'MainBaccarat',
  card: 'noHIstory',
  betting: 'MainBaccarat',
  top: 'noHIstory',
  winlose: 'Button',
} as const;

const STATE_MACHINES = {
  main: 'Main',
  card: 'Card',
  betting: 'Main',
  top: 'Main',
  winlose: 'animationManager',
} as const;

/**
 * Per-layer fit — mirrors packages/amgbaccarat_v2 game_screen.dart exactly:
 *   main background → Fit.cover   (line 581)
 *   card layer      → Fit.cover   (line 610)
 *   top layer       → Fit.cover   (line 620)
 *   betting UI      → Fit.contain (line 600, positioned in a 70%×70% box)
 *
 * The MainBaccarat / noHIstory artboards are NOT authored at the 1792×828
 * (2.16:1) stage aspect ratio, so Fit.Contain shrinks them to fit entirely
 * inside the full-screen canvas and centers them — leaving the table tiny in a
 * sea of black. Cover fills the canvas (cropping overflow) the way Flutter does.
 * (Dragon Tiger's port uses Contain throughout only because dragontiger.riv's
 * Main artboard happens to match the stage aspect; baccarat's does not.)
 */
const FITS: Record<TargetKey, Fit> = {
  main: Fit.Cover,
  card: Fit.Cover,
  betting: Fit.Contain,
  top: Fit.Cover,
  // The MainWinLose "Button" artboard is a full-screen, transparent popup
  // overlay (StartBet / NoMoreBet). Contain keeps the centered popup graphic
  // uncropped, the same way Dragon Tiger / Roulette load this file.
  winlose: Fit.Contain,
};

export interface RiveLayerOptions {
  canvases: Record<TargetKey, HTMLCanvasElement>;
  assetBase?: string;
}

export type RiveEventTopic =
  | 'ready'
  | 'placeBetClick'
  | 'evtClickChips'
  | 'evtClearClick'
  | 'evtButtonPlayClick'
  | 'UIButtonClick'
  | 'evtResultStop'
  | 'evtYouWinEnd'
  | 'evtYouLoseEnd'
  | 'sideAnimWin'
  | 'allControllersReady';

type RiveEventCallback = (data: Record<string, unknown>) => void;

export class RiveLayer {
  private readonly canvases: Record<TargetKey, HTMLCanvasElement>;
  private readonly assetBase: string;
  private readonly targets: Map<TargetKey, RiveTarget> = new Map();
  private readonly listeners: Map<string, Set<RiveEventCallback>> = new Map();

  /**
   * Text-run name → where it actually lives (top-level or nested under a path),
   * plus how many times we've probed. Resolved by silent probing and cached
   * once found; an empty result is NOT locked in until RESOLVE_TRIES probes,
   * because a run can be un-queryable for the first frame or two after load.
   */
  private readonly textRunTargets: Map<
    string,
    { placements: TextRunPlacement[]; tries: number }
  > = new Map();

  /** Max times a still-unresolved text run is re-probed before giving up. */
  private static readonly RESOLVE_TRIES = 30;

  /**
   * Nested-artboard instance names to search when a text run isn't a top-level
   * run. baccarat.riv keeps the min/max table-limit runs (txtMinValue /
   * txtMaxValue) inside the `MinMax` nested artboard on MainBaccarat — exactly
   * like Dragon Tiger. The web runtime's setTextRunValue does NOT descend into
   * nested artboards (unlike Flutter's artboard.setText, which auto-descends),
   * so they must be set via setTextRunValueAtPath(name, value, 'MinMax'). The
   * rest are cheap extra guesses for other nested labels.
   */
  private static readonly TEXT_RUN_PATHS = ['MinMax', 'Table Limit', 'TableLimit', 'BetPanel'];

  constructor(opts: RiveLayerOptions) {
    this.canvases = opts.canvases;
    this.assetBase = opts.assetBase ?? '/assets/Rive/';
  }

  async init(): Promise<void> {
    this.fitAllCanvases();
    window.addEventListener('resize', () => this.fitAllCanvases());
    this.attachContextLossHandlers();

    await Promise.all([
      this.loadOne('main'),
      this.loadOne('card'),
      this.loadOne('betting'),
      this.loadOne('top'),
      this.loadOne('winlose'),
    ]);

    this.dumpInputs();
    // Keep the .riv's sound branch on so it always fires its sound-cue events
    // (DrawCard / DrawCardSingle / FlipCard / CollectCard). Actual audio is
    // played by SoundManager; the on-screen toggle gates that, not this input —
    // so we leave it true.
    this.setSoundOn(true);
    this.emit('ready', {});
    this.emit('allControllersReady', {});
  }

  /**
   * Re-arm rendering on every canvas when the tab becomes visible again.
   *
   * Rive's own per-instance visibilitychange handler already resumes the rAF
   * loop here (the autoplay state machines keep `isPlaying` true), so this is
   * defensive belt-and-suspenders: it guarantees an immediate repaint at the
   * current surface size the instant the tab returns — before
   * GameOrchestrator.resyncToCurrentState() re-drives the inputs to snap the
   * visuals to the current state — and stays correct even if a future .riv
   * export ships a state machine that settles to a stopped state.
   * Best-effort, never throws (mirrors fitAllCanvases).
   */
  onVisible(): void {
    for (const t of this.targets.values()) {
      try {
        t.instance.startRendering();
        t.instance.resizeDrawingSurfaceToCanvas();
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * Defensive WebGL context-loss handling. With 5 Rive WebGL2 canvases plus the
   * Pixi canvas in one tab, a backgrounded tab can have its GPU context reclaimed
   * by the browser. preventDefault() on `webglcontextlost` lets the browser
   * attempt restoration instead of permanently killing the canvas; on
   * `webglcontextrestored` we re-arm rendering for that target.
   */
  private attachContextLossHandlers(): void {
    for (const [key, canvas] of Object.entries(this.canvases)) {
      canvas.addEventListener('webglcontextlost', (e) => {
        e.preventDefault();
        warn('[Rive] WebGL context lost on', key);
      });
      canvas.addEventListener('webglcontextrestored', () => {
        warn('[Rive] WebGL context restored on', key, '— re-arming');
        const t = this.targets.get(key as TargetKey);
        if (!t) return;
        try {
          t.instance.startRendering();
          t.instance.resizeDrawingSurfaceToCanvas();
        } catch {
          /* ignore */
        }
      });
    }
  }

  private fitAllCanvases(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    for (const canvas of Object.values(this.canvases)) {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
    }
    for (const t of this.targets.values()) {
      try {
        t.instance.resizeDrawingSurfaceToCanvas();
      } catch {
        /* ignore */
      }
    }
  }

  private loadOne(key: TargetKey): Promise<void> {
    return new Promise((resolve, reject) => {
      const file = FILES[key];
      const artboard = ARTBOARDS[key];
      const sm = STATE_MACHINES[key];
      const canvas = this.canvases[key];
      const src = `${this.assetBase}${file}`;
      const rive = new Rive({
        canvas,
        src,
        artboard,
        stateMachines: sm,
        autoplay: true,
        layout: new Layout({ fit: FITS[key], alignment: Alignment.Center }),
        onLoad: () => {
          try {
            const inputs = this.cacheInputs(rive, sm);
            const target: RiveTarget = {
              key,
              file,
              stateMachine: sm,
              instance: rive,
              inputs,
            };
            this.targets.set(key, target);
            this.attachEventBridge(rive, key);
            try {
              rive.resizeDrawingSurfaceToCanvas();
            } catch {
              /* ignore */
            }
            resolve();
          } catch (e) {
            reject(e);
          }
        },
        onLoadError: (err: unknown) => {
          reject(new Error(`Failed to load ${src} [${artboard}/${sm}]: ${String(err)}`));
        },
      });
    });
  }

  private cacheInputs(rive: Rive, sm: string): Map<string, AnyInput> {
    const out = new Map<string, AnyInput>();
    let list: unknown[] = [];
    try {
      list = rive.stateMachineInputs(sm) ?? [];
    } catch (e) {
      warn('[Rive] stateMachineInputs failed for', sm, e);
      return out;
    }
    for (const raw of list) {
      const inp = raw as {
        name: string;
        type: number;
        value: number | boolean;
        fire(): void;
      };
      switch (inp.type) {
        case StateMachineInputType.Number:
          out.set(inp.name, {
            type: 'number',
            name: inp.name,
            set: (v) => { inp.value = v; },
            get: () => Number(inp.value ?? 0),
          });
          break;
        case StateMachineInputType.Boolean:
          out.set(inp.name, {
            type: 'boolean',
            name: inp.name,
            set: (v) => { inp.value = v; },
            get: () => Boolean(inp.value),
          });
          break;
        case StateMachineInputType.Trigger:
          out.set(inp.name, {
            type: 'trigger',
            name: inp.name,
            fire: () => inp.fire(),
          });
          break;
        default:
          debugLog('[Rive] unknown input type', inp.type, 'for', inp.name);
      }
    }
    return out;
  }

  private dumpInputs(): void {
    for (const t of this.targets.values()) {
      const rows = Array.from(t.inputs.values()).map((i) => ({
        target: t.key,
        file: t.file,
        sm: t.stateMachine,
        name: i.name,
        type: i.type,
      }));
      // eslint-disable-next-line no-console
      console.groupCollapsed(
        `[Rive] ${t.key} (${t.file} / ${t.stateMachine}) — ${rows.length} input(s)`,
      );
      // eslint-disable-next-line no-console
      console.table(rows);
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  }

  private attachEventBridge(rive: Rive, target: TargetKey): void {
    try {
      rive.on(EventType.RiveEvent, (event: unknown) => {
        const e = event as { data?: { name?: string; properties?: Record<string, unknown> } };
        const name = e?.data?.name;
        if (!name) return;
        const props = { ...(e?.data?.properties ?? {}), __target: target };
        if (this.listeners.has(name)) {
          this.emit(name, props);
        } else {
          debugLog('[Rive][event:passthrough]', target, name, props);
        }
      });
    } catch (e) {
      warn('[Rive] event bridge attach failed', e);
    }
  }

  // -----------------------------------------------------------------
  // Public high-level API — mirrors RiveControllerManager
  // -----------------------------------------------------------------

  /**
   * Set all six card values at once (player slots 0..2, banker slots 0..2).
   * Cards live under nested path "CardType" — see
   *   rive_controller_manager.dart:241-255 (_setNumber CardType fallback).
   * 0 means absent.
   */
  setCards(playerCards: ReadonlyArray<number>, bankerCards: ReadonlyArray<number>): void {
    this.setCardValue(CardInputs.numPcard1, playerCards[0] ?? 0);
    this.setCardValue(CardInputs.numPcard2, playerCards[1] ?? 0);
    this.setCardValue(CardInputs.numPcard3, playerCards[2] ?? 0);
    this.setCardValue(CardInputs.numBcard1, bankerCards[0] ?? 0);
    this.setCardValue(CardInputs.numBcard2, bankerCards[1] ?? 0);
    this.setCardValue(CardInputs.numBcard3, bankerCards[2] ?? 0);
  }

  /**
   * Set one card slot's face value.
   *
   * The .riv nests a single reusable card artboard whose `CardType` number
   * input (1..52) selects the face (ClubCard1..K, SpadeCard1..K, …). That
   * artboard is instanced at a PATH per slot — PlayerCard1..3 / BankerCard1..3
   * (verified by binary introspection of baccarat.riv). So the value is set as
   * input `CardType` AT PATH `slotPath`, the exact shape Dragon Tiger uses
   * (apps/amg_dragontiger_pixi setCards → setNumberStateAtPath('CardType', v,
   * 'DragonCard')).
   *
   * Trap that caused every card to show as Ace: the old code tried the SWAPPED
   * form setNumberStateAtPath('PlayerCard1', v, 'CardType') first. The WebGL2
   * runtime does NOT throw on a path miss — it console.warns and returns void —
   * so trySetNumberAtPathSilent saw "no throw" as success and `continue`d
   * before the correct inverse form ran. CardType stayed 0, so each revealed
   * card fell back to its default face (Ace). We now set the correct form
   * directly on the noHIstory-backed `card` (visible, z-4) and `top` (z-5)
   * layers — the two controllers that carry the card instances.
   */
  private setCardValue(slotPath: string, value: number): void {
    this.trySetNumberAtPathSilent('card', CardInputs.nestedPath, value, slotPath);
    this.trySetNumberAtPathSilent('top', CardInputs.nestedPath, value, slotPath);
  }

  /**
   * Reveal player card 1..3. Mirrors revealPlayerCard
   * (rive_controller_manager.dart:320-330): sets CardPlayerBanker to
   * the right code (P1=1, P2=4, P3=3) and fires CardPlayerBankerTrigger
   * on both `card` and `top`.
   */
  revealPlayerCard(index: 1 | 2 | 3): void {
    const codes = [1, 4, 3];
    this.revealCardWithCode(codes[index - 1]);
  }

  /** Reveal banker card 1..3. Codes B1=2, B2=5, B3=6. */
  revealBankerCard(index: 1 | 2 | 3): void {
    const codes = [2, 5, 6];
    this.revealCardWithCode(codes[index - 1]);
  }

  private revealCardWithCode(code: number): void {
    for (const target of ['card', 'top'] as TargetKey[]) {
      this.setNumberAny(target, CardInputs.numCardPlayerBanker, code);
      this.fireTriggerAny(target, CardInputs.trigCardPlayerBanker);
    }
  }

  /**
   * Show the two-digit Player vs Banker score, e.g. 5 vs 8 → 05 vs 08:
   * NumberLeftTens=0/NumberLeftUnits=5 (player, left) and
   * NumberRightTens=0/NumberRightUnits=8 (banker, right).
   *
   * Each score digit is an instance of the SAME reusable card/number component
   * the cards use (see setCardValue): the digit value 0..9 is the component's
   * `CardType` input, set AT the digit's instance PATH — NumberLeftTens /
   * NumberLeftUnits / NumberRightTens / NumberRightUnits — NOT the other way
   * round. (Verified by loading baccarat.riv headlessly: only
   * inputByPath('CardType','NumberLeftTens') resolves; the inverse form the old
   * code used, inputByPath('NumberLeftTens','CardType'), hits nothing — which is
   * why the winner panel stayed at 00 vs 00.) The instances live on the
   * noHIstory artboard, i.e. the `card` (z-4) and `top` (z-5) targets.
   */
  setScores(playerScore: number, bankerScore: number): void {
    const p = String(Math.max(0, Math.min(99, Math.floor(playerScore)))).padStart(2, '0');
    const b = String(Math.max(0, Math.min(99, Math.floor(bankerScore)))).padStart(2, '0');
    const digits: ReadonlyArray<readonly [string, number]> = [
      [ScoreInputs.numLeftTens, +p[0]],
      [ScoreInputs.numLeftUnits, +p[1]],
      [ScoreInputs.numRightTens, +b[0]],
      [ScoreInputs.numRightUnits, +b[1]],
    ];
    for (const target of ['card', 'top'] as TargetKey[]) {
      for (const [instancePath, digit] of digits) {
        this.trySetNumberAtPathSilent(target, CardInputs.nestedPath, digit, instancePath);
      }
    }
  }

  /**
   * Animate the cards back into the deck. `type` follows baccarat_engine's
   * clearCardType (1 = 3+3, 2 = 2+3, 3 = 3+2, 4 = 2+2).
   * Mirrors clearCard (rive_controller_manager.dart:370-389) — fires on card
   * first, falls back to main.
   */
  clearCard(type: 1 | 2 | 3 | 4): void {
    let fired = false;
    for (const target of ['card', 'top'] as TargetKey[]) {
      const ok =
        this.setNumberAny(target, CardInputs.numClearCard, type) ||
        this.trySetNumberAtPathSilent(target, CardInputs.numClearCard, type, CardInputs.nestedPath);
      const fireOk = this.fireTriggerAny(target, CardInputs.trigClearCard);
      if (ok && fireOk) { fired = true; break; }
    }
    if (!fired) {
      // Legacy main fallback (rive_controller_manager.dart:386-388).
      this.setNumberAny('main', MainInputs.numClearType, type);
      this.fireTriggerAny('main', MainInputs.trigClearCard);
    }
  }

  /**
   * Fire the "place your bets" (StartBet) popup on the MainWinLose.riv overlay.
   * Updated contract: set BetPanelNumber = 1, then fire BetPanelTrigger
   * (`isSoundBetPanel` is no longer used). The artboard emits a `StartBet` Rive
   * event when the popup plays, which SoundManager turns into StartBet.mp3.
   */
  fireStartBet(): void {
    this.setNumberAny('winlose', WinLoseInputs.betPanelNumber, BetPanelState.startBet);
    this.fireTriggerAny('winlose', WinLoseInputs.trigBetPanel);
  }

  /**
   * Fire the "no more bets" (NoMoreBet) popup on the MainWinLose.riv overlay:
   * BetPanelNumber = 2, then fire BetPanelTrigger. Emits a `NoMoreBet` Rive
   * event → NoMoreBet.mp3.
   */
  fireNoMoreBet(): void {
    this.setNumberAny('winlose', WinLoseInputs.betPanelNumber, BetPanelState.noMoreBet);
    this.fireTriggerAny('winlose', WinLoseInputs.trigBetPanel);
  }

  /**
   * Fire the winner banner on the `top` controller AND the win-area
   * highlight on `betting`. Mirrors triggerWinnerAnimation +
   * showWinAnimation (rive_controller_manager.dart:432-602).
   */
  triggerWinner(side: RiveSide): void {
    switch (side) {
      case 'player':
        this.fireTriggerAny('top', MainInputs.trigPlayerWin);
        this.fireTriggerAny('betting', BettingInputs.trigWinPlayer);
        break;
      case 'banker':
        this.fireTriggerAny('top', MainInputs.trigBankerWin);
        this.fireTriggerAny('betting', BettingInputs.trigWinBanker);
        break;
      case 'tie':
        this.fireTriggerAny('top', MainInputs.trigTie);
        this.fireTriggerAny('betting', BettingInputs.trigWinTie);
        break;
    }
  }

  /** Plays the girl animation. 0=idle, 1=start game, 2=end game. */
  triggerGirlAnim(state: 0 | 1 | 2): void {
    if (state <= 0) return;
    // Try main first, then top (Flutter does both — rive_controller_manager.dart:408-421).
    for (const target of ['main', 'top'] as TargetKey[]) {
      this.setNumberAny(target, MainInputs.numGirlAnim, state);
      if (this.fireTriggerAny(target, MainInputs.trigGirlAnim)) break;
    }
  }

  /** Highlight a bet area on the betting artboard. */
  highlightBetArea(betId: number): void {
    let name: string | null = null;
    switch (betId) {
      case 265: name = BettingInputs.trigPlayer; break;
      case 266: name = BettingInputs.trigBanker; break;
      case 267: name = BettingInputs.trigTie; break;
      case 268: name = BettingInputs.trigPlayerPair; break;
      case 269: name = BettingInputs.trigBankerPair; break;
      default: return;
    }
    this.fireTriggerAny('betting', name);
  }

  /**
   * Show the YOU WIN / YOU LOSE popup with an amount on the MainWinLose.riv
   * `winlose` overlay — the SAME overlay that carries StartBet / NoMoreBet.
   *
   * Set the `txtWonAmount` text run, set `YouWinLose` (1 = win, 2 = lose), then
   * fire `YouWinLoseTrigger`. The overlay emits `evtYouWinEnd` / `evtYouLoseEnd`
   * when the popup finishes (the orchestrator listens for those to tear the
   * round down). Mirrors amg_dragontiger_pixi / amg_carracing_pixi.
   *
   * (The Flutter game drove this on the betting artboard, but this port's
   * baccaratbettingui.riv is a bet-zones-only export with no YouWinLose inputs
   * or popup graphic — driving it there silently did nothing. The proven popup
   * lives on MainWinLose.riv.)
   */
  showWinLose(isWin: boolean, formattedAmount: string): void {
    const winlose = this.targets.get('winlose');
    if (!winlose) return;
    try {
      winlose.instance.setTextRunValue(WinLoseInputs.txtWonAmount, formattedAmount);
    } catch {
      /* txtWonAmount not a text run on this build — skip */
    }
    this.setNumberAny('winlose', WinLoseInputs.youWinLose, isWin ? 1 : 2);
    this.fireTriggerAny('winlose', WinLoseInputs.trigYouWinLose);
  }

  /**
   * Set a text run on every artboard that exposes it — top-level OR nested
   * under one of TEXT_RUN_PATHS.
   *
   * The web runtime's `setTextRunValue` only finds TOP-LEVEL runs and
   * `console.warn`s on a miss; nested runs (e.g. the min/max table limits inside
   * the `MinMax` artboard) need `setTextRunValueAtPath`. We resolve where each
   * run lives by silently probing, cache the placements, and only ever set on
   * confirmed placements. The probe re-runs while nothing has matched yet (a run
   * can be un-queryable for the first frame or two after load) so we never lock
   * in an early miss. Mirrors amg_dragontiger_pixi.
   */
  private setTextRunBroadcast(name: string, value: string): void {
    let entry = this.textRunTargets.get(name);
    if (!entry || (entry.placements.length === 0 && entry.tries < RiveLayer.RESOLVE_TRIES)) {
      const placements: TextRunPlacement[] = [];
      for (const key of this.targets.keys()) {
        if (this.hasTextRun(key, name)) {
          placements.push({ key });
          continue;
        }
        for (const path of RiveLayer.TEXT_RUN_PATHS) {
          if (this.hasTextRunAtPath(key, name, path)) {
            placements.push({ key, path });
            break; // first matching path per target is enough
          }
        }
      }
      entry = { placements, tries: (entry?.tries ?? 0) + 1 };
      this.textRunTargets.set(name, entry);
    }
    for (const p of entry.placements) {
      const inst = this.targets.get(p.key)?.instance;
      if (!inst) continue;
      try {
        if (p.path) inst.setTextRunValueAtPath(name, value, p.path);
        else inst.setTextRunValue(name, value);
      } catch {
        /* placement went stale (controller disposed) — ignore */
      }
    }
  }

  setBalanceText(value: string): void {
    // txtRCoinAmount on the `top` artboard (TopInputs.txtBalance).
    this.setTextRunBroadcast(TopInputs.txtBalance, value);
    // Fallbacks for other .riv revisions.
    this.setTextRunBroadcast('txtBalance', value);
    this.setTextRunBroadcast('txtCredit', value);
  }

  setBetAmountText(value: string): void {
    this.setTextRunBroadcast(TopInputs.txtBet, value);
    this.setTextRunBroadcast('txtBet', value);
  }

  setWinAmountText(value: string): void {
    this.setTextRunBroadcast(TopInputs.txtWin, value);
    this.setTextRunBroadcast('txtWin', value);
  }

  /**
   * Min / max bet text. baccarat.riv keeps `txtMinValue` / `txtMaxValue` inside
   * the `MinMax` nested artboard on MainBaccarat — the Flutter manager sets them
   * via `_mainController.artboard.setText(...)`, which auto-descends into nested
   * artboards (rive_controller_manager.dart:520-537). The web runtime's
   * setTextRunValue does NOT descend, so we route through setTextRunBroadcast,
   * which probes the nested TEXT_RUN_PATHS (incl. `MinMax`) and writes via
   * setTextRunValueAtPath. `txtMinValue` / `txtMaxValue` are the real run names
   * (verified via Tools/rive introspect); the rest are harmless fallbacks for
   * other .riv revisions. Mirrors amg_dragontiger_pixi.
   */
  setBetLimits(minText: string, maxText: string): void {
    const minCandidates = ['txtMinValue', 'txtMinBet', 'txtMin', 'txtMinLimit'];
    const maxCandidates = ['txtMaxValue', 'txtMaxBet', 'txtMax', 'txtMaxLimit'];
    for (const n of minCandidates) this.setTextRunBroadcast(n, minText);
    for (const n of maxCandidates) this.setTextRunBroadcast(n, maxText);
  }

  /**
   * 96-slot history. Values 0=hidden, 1=player win, 2=banker win, 3=tie
   * (we use a tri-state mapping like Dragon Tiger; the real game encodes
   * extra pair info into 4-9, but those don't affect single-player
   * gameplay).
   */
  setHistory(zeroBasedIndex: number, value: 0 | 1 | 2 | 3): void {
    if (zeroBasedIndex < 0 || zeroBasedIndex >= HistoryInputs.totalSlots) return;
    const name = HistoryInputs.nameAt(zeroBasedIndex);
    // Try top-level cached, then nested at various paths (some exports
    // place the history bar under its own state-machine path).
    if (this.trySetNumberSilent('main', name, value)) return;
    // NOTE (commented out for now): the path-based fallback below calls
    // Rive's setNumberStateAtPath, which logs "Could not access an input
    // with name: 'numHistoryNN'" to the console *itself* before throwing —
    // so the try/catch swallows the exception but not the log line. The
    // higher history slots (~76-96) aren't defined in baccarat.riv at all,
    // so every round spammed the console. The slots that exist resolve via
    // the top-level trySetNumberSilent above, so this fallback never
    // actually succeeded; it was pure noise. Re-enable if a future .riv
    // export nests the history bar under its own state-machine path.
    // const PATHS = ['result', 'Result', 'history', 'History', 'bar'];
    // for (const p of PATHS) {
    //   if (this.trySetNumberAtPathSilent('main', name, value, p)) return;
    // }
  }

  /** Replace recent history slots (oldest first) and zero out the rest. */
  setAllHistory(results: ReadonlyArray<1 | 2 | 3>): void {
    for (let i = 0; i < HistoryInputs.totalSlots; i++) {
      const v = (i < results.length ? results[i] : 0) as 0 | 1 | 2 | 3;
      this.setHistory(i, v);
    }
  }

  clearAllHistory(): void {
    for (let i = 0; i < HistoryInputs.totalSlots; i++) this.setHistory(i, 0);
  }

  /**
   * Drive the `IsSoundOn` boolean input on every artboard that exposes it.
   * This gates whether the .riv emits its sound-cue events; we keep it true so
   * those cues always reach SoundManager. No-ops on artboards without the input.
   * The input name was verified against baccarat.riv via `Tools/rive introspect`.
   */
  setSoundOn(on: boolean): void {
    for (const key of this.targets.keys()) {
      this.setBoolAny(key, 'IsSoundOn', on);
    }
  }

  // -----------------------------------------------------------------
  // Pub/sub
  // -----------------------------------------------------------------

  on(topic: RiveEventTopic | string, cb: RiveEventCallback): () => void {
    let set = this.listeners.get(topic);
    if (!set) {
      set = new Set();
      this.listeners.set(topic, set);
    }
    set.add(cb);
    return () => set!.delete(cb);
  }

  private emit(topic: string, data: Record<string, unknown>): void {
    const set = this.listeners.get(topic);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(data);
      } catch (e) {
        warn('[Rive listener]', e);
      }
    }
  }

  // -----------------------------------------------------------------
  // Low-level input plumbing
  // -----------------------------------------------------------------

  private setNumberAny(key: TargetKey, name: string, value: number): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'number') { inp.set(value); return true; }
    return false;
  }

  private setBoolAny(key: TargetKey, name: string, value: boolean): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'boolean') { inp.set(value); return true; }
    if (inp) {
      debugLog(`[Rive] '${name}' on ${key} is ${inp.type}, not boolean`);
      return false;
    }
    debugLog(`[Rive] bool '${name}' not found on ${key} (${t.file})`);
    return false;
  }

  private fireTriggerAny(key: TargetKey, name: string): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'trigger') { inp.fire(); return true; }
    return false;
  }

  private trySetNumberSilent(key: TargetKey, name: string, value: number): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'number') { inp.set(value); return true; }
    return false;
  }

  private trySetNumberAtPathSilent(
    key: TargetKey,
    inputName: string,
    value: number,
    path: string,
  ): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    try {
      t.instance.setNumberStateAtPath(inputName, value, path);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Silently check whether an artboard exposes a queryable TOP-LEVEL text run,
   * WITHOUT triggering setTextRunValue's console.warn on a miss. Primary probe:
   * the artboard's own `textRun` (returns falsy silently); fallback: the public
   * `getTextRunValue` under warning suppression. Mirrors amg_dragontiger_pixi.
   */
  private hasTextRun(key: TargetKey, name: string): boolean {
    const inst = this.targets.get(key)?.instance;
    if (!inst) return false;
    const artboard = (inst as unknown as { artboard?: { textRun?: (n: string) => unknown } })
      .artboard;
    if (artboard && typeof artboard.textRun === 'function') {
      try {
        return !!artboard.textRun(name);
      } catch {
        /* fall through to the public-API probe */
      }
    }
    return RiveLayer.silenceRiveAccessWarnings(() => {
      try {
        return inst.getTextRunValue(name) !== undefined;
      } catch {
        return false;
      }
    });
  }

  /**
   * Silently check whether a text run exists nested under `path` (a nested
   * artboard instance name). Primary probe: the artboard's own `textByPath`;
   * fallback: the public `getTextRunValueAtPath` under warning suppression.
   * Mirrors amg_dragontiger_pixi.
   */
  private hasTextRunAtPath(key: TargetKey, name: string, path: string): boolean {
    const inst = this.targets.get(key)?.instance;
    if (!inst) return false;
    const artboard = (
      inst as unknown as { artboard?: { textByPath?: (n: string, p: string) => unknown } }
    ).artboard;
    if (artboard && typeof artboard.textByPath === 'function') {
      try {
        return !!artboard.textByPath(name, path);
      } catch {
        /* fall through to the public-API probe */
      }
    }
    return RiveLayer.silenceRiveAccessWarnings(() => {
      try {
        return inst.getTextRunValueAtPath(name, path) !== undefined;
      } catch {
        return false;
      }
    });
  }

  /**
   * Run `fn` with Rive's "Could not access/get/set … / Tried to access …"
   * console warnings suppressed (restoring console.warn afterwards). Used only
   * around existence probes so a missing run never reaches the console.
   */
  private static silenceRiveAccessWarnings<T>(fn: () => T): T {
    const original = console.warn;
    console.warn = (...args: unknown[]): void => {
      const first = typeof args[0] === 'string' ? args[0] : '';
      if (/^(Could not (access|get|set)|Tried to access)\b/.test(first)) return;
      (original as (...a: unknown[]) => void).apply(console, args);
    };
    try {
      return fn();
    } finally {
      console.warn = original;
    }
  }
}
